import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import DeleteModal from '../../../components/modals/DeleteModal';
import SenderModal from '../components/modals/SenderModal';
import PostalFormModal from '../../postalForm/components/PostalFormModal';
import { deleteSender, fetchSenders } from '../api/sendersApi';
import { useAuth } from '../../../context/useAuth';
import style from '../../users/components/users.module.scss';
import { PAGE_SIZE } from '../../../config';
import { isStaff } from '../../../shared/utils/roles';

const getSenderLocation = (sender) =>
  [sender?.postalCode, sender?.city].filter(Boolean).join(' ');

export default function SendersPage() {
  const { user } = useAuth();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [senders, setSenders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [isSenderModalOpen, setIsSenderModalOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [senderToDelete, setSenderToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDone, setDeleteDone] = useState(false);
  const [isPostalFormOpen, setIsPostalFormOpen] = useState(false);
  const latestSendersRequestRef = useRef(0);

  const canManage = isStaff(user);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadSenders = useCallback(
    async (page = currentPage) => {
      const requestId = latestSendersRequestRef.current + 1;
      latestSendersRequestRef.current = requestId;
      setListLoading(true);

      const { data, ok, message } = await fetchSenders({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch,
      });

      if (requestId !== latestSendersRequestRef.current) {
        return;
      }

      setListLoading(false);

      if (ok && data) {
        const resolvedPage = data?.pagination?.currentPage || 1;
        setSenders(data.senders || []);
        if (resolvedPage !== page) {
          setCurrentPage(resolvedPage);
        }
        setTotalPages(data?.pagination?.totalPages || 1);
        setTotalResults(data?.pagination?.totalCount ?? null);
      } else if (message) {
        setMessage(message);
      }
    },
    [currentPage, debouncedSearch]
  );

  useEffect(() => {
    loadSenders(currentPage);
  }, [loadSenders]);

  const handleCreateOpen = () => {
    setSelectedSender(null);
    setIsSenderModalOpen(true);
  };

  const handleEditOpen = (sender) => {
    setSelectedSender(sender);
    setIsSenderModalOpen(true);
  };

  const handleSenderModalClose = () => {
    setSelectedSender(null);
    setIsSenderModalOpen(false);
  };

  const handleDeleteOpen = (sender) => {
    setSenderToDelete(sender);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setSenderToDelete(null);
    setDeleteLoading(false);
    setDeleteDone(false);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteSender = async () => {
    if (!senderToDelete?._id) return;

    setDeleteLoading(true);
    const response = await deleteSender(senderToDelete._id);

    if (!response.ok) {
      setMessage(response.message);
      setDeleteLoading(false);
      return;
    }

    setDeleteDone(true);
    setMessage(response.message || 'Posiljalac obrisan.');
    await loadSenders();

    setTimeout(() => {
      handleDeleteClose();
    }, 700);
  };

  const columns = useMemo(
    () => [
      { key: 'fullName', label: 'Ime i Prezime', render: (val) => val || '-' },
      { key: 'street', label: 'Ulica', render: (val) => val || '-' },
      {
        key: 'postalCode',
        label: 'Poštanski broj / Grad',
        render: (_, row) => getSenderLocation(row) || '-',
      },
      { key: 'phone', label: 'Telefon', render: (val) => val || '-' },
      { key: 'bankAccount', label: 'Žiro račun', render: (val) => val || '-' },
    ],
    []
  );

  return (
    <>
      <ContentLayout>
        <TableSection
          showCreateButton={canManage}
          createButtonDisplay="Dodaj posiljaoca"
          onCreateClick={handleCreateOpen}
          headerActions={
            <Button
              type="primary"
              className={style.users__header__addButton}
              onClick={() => setIsPostalFormOpen(true)}
            >
              Obrazac za poštu
            </Button>
          }
          onSearch={setSearch}
          columns={columns}
          actions={[
            {
              key: 'edit',
              label: 'Izmeni posiljaoca',
              icon: (
                <img
                  src="/assets/icons/edit-icon.svg"
                  alt="Izmeni"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleEditOpen,
              visible: () => canManage,
            },
            {
              key: 'delete',
              label: 'Obrisi posiljaoca',
              icon: (
                <img
                  src="/assets/icons/delete.svg"
                  alt="Obriši"
                  width={16}
                  height={16}
                  className="dropdown-icon"
                />
              ),
              onClick: handleDeleteOpen,
              visible: () => canManage,
              danger: true,
            },
          ]}
          data={senders}
          emptyState={{
            icon: '/assets/icons/no-users.svg',
            title: 'Trenutno nema posiljalaca',
            description: 'Dodajte posiljaoca.',
          }}
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
          searchPlaceholder="Pretraži pošiljaoce"
        />
      </ContentLayout>
      <SenderModal
        isOpen={isSenderModalOpen}
        onClose={handleSenderModalClose}
        onSaved={loadSenders}
        sender={selectedSender}
      />
      {/* Obrazac za postu bez pacijenta: primalac se upisuje rucno, bez napomene. */}
      <PostalFormModal
        isOpen={isPostalFormOpen}
        onClose={() => setIsPostalFormOpen(false)}
        manualRecipient
      />
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDeleteSender}
        onCancel={handleDeleteClose}
        loading={deleteLoading}
        done={deleteDone}
        title="Brisanje posiljaoca"
        message={`Da li ste sigurni da zelite da obrisete posiljaoca "${
          senderToDelete?.fullName || ''
        }"?`}
        buttonText="Obrisi posiljaoca"
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
